import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ExternalLink, Copy, KeyRound, Pencil, LogOut, X, UtensilsCrossed, Search, MessageCircle, Trash2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { mediaUrl, maskPhoneBR, localDigitsBR, waNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup } from "@/components/Brand";

const STATUS = {
  em_implantacao: { label: "Em implantação", cls: "bg-sky-500/15 text-sky-300" },
  ativo: { label: "Ativo", cls: "bg-emerald-500/15 text-emerald-300" },
  pagamento_pendente: { label: "Pagamento pendente", cls: "bg-amber-500/15 text-amber-300" },
  suspenso: { label: "Suspenso", cls: "bg-orange-500/15 text-orange-300" },
  cancelado: { label: "Cancelado", cls: "bg-rose-500/15 text-rose-300" },
};

export default function Admin() {
  const { logout, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [tab, setTab] = useState("restaurants");
  const [query, setQuery] = useState("");
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/restaurants").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  };
  const loadLeads = () => {
    setLeadsLoading(true);
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLeadsLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { if (tab === "leads") loadLeads(); }, [tab]);
  const filtered = useMemo(() => rows.filter((r) => {
    const q = query.trim().toLowerCase();
    return !q || `${r.name} ${r.responsible_name} ${r.email}`.toLowerCase().includes(q);
  }), [rows, query]);

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/${slug}`);
    toast.success("Link copiado");
  };
  const resetAccess = async (r) => {
    try {
      const { data } = await api.post(`/admin/restaurants/${r.id}/reset-access`);
      toast.success(`Nova senha temporária: ${data.temp_password}`, { duration: 12000 });
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen bg-[#0C0C0E] text-zinc-100">
      <header className="border-b border-white/5 bg-[#121215]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLockup />
            <span className="text-xs bg-white/10 px-2 py-1 rounded-full">Superadmin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-zinc-400 hidden sm:block">{user?.name}</span>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/15 px-3 py-2 rounded-full"><LogOut size={15} /> Sair</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex gap-2">
            <button onClick={() => setTab("restaurants")} data-testid="admin-tab-restaurants" className={`px-4 py-2 rounded-full text-sm font-bold ${tab === "restaurants" ? "hmmm-gradient text-white" : "bg-white/5 text-zinc-400"}`}>Restaurantes</button>
            <button onClick={() => setTab("leads")} data-testid="admin-tab-leads" className={`px-4 py-2 rounded-full text-sm font-bold ${tab === "leads" ? "hmmm-gradient text-white" : "bg-white/5 text-zinc-400"}`}>Leads</button>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="new-restaurant-btn" className="flex items-center gap-1.5 rounded-full hmmm-gradient px-3.5 py-2 text-sm font-bold hmmm-glow text-white active:scale-95 transition-transform"><Plus size={16} /> Novo</button>
        </div>

        {tab === "restaurants" ? (
          <>
          <div className="flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 mb-4">
            <Search size={16} className="text-zinc-500" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-search" placeholder="Buscar por nome, responsável ou e-mail..." className="flex-1 bg-transparent outline-none text-sm" />
          </div>

        {/* Desktop table */}
        <div className="hidden md:block rounded-2xl border border-white/10 bg-[#121215] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="restaurants-table">
              <thead className="bg-white/[0.03] text-zinc-400 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Restaurante</th>
                  <th className="text-left px-4 py-3">Responsável</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">E-mail</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Vencimento</th>
                  <th className="text-left px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Carregando...</td></tr>}
                {!loading && filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Nenhum restaurante ainda.</td></tr>}
          {filtered.map((r) => {
                  const st = STATUS[r.status] || STATUS.em_implantacao;
                  return (
                    <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`restaurant-row-${r.id}`}>
                      <td className="px-4 py-3 font-semibold">
                        <span className="flex items-center gap-2.5">
                          <span className="h-8 w-8 rounded-lg overflow-hidden bg-[#141417] flex items-center justify-center shrink-0">{r.logo ? <img src={mediaUrl(r.logo)} alt="" className="h-full w-full object-cover" /> : <UtensilsCrossed size={15} className="hmmm-text" />}</span>
                          {r.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-400">{r.responsible_name}</td>
                      <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{r.email}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span></td>
                      <td className="px-4 py-3 text-zinc-400 hidden lg:table-cell">{r.due_date || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Act tid={`edit-${r.id}`} onClick={() => setEditRow(r)} title="Editar"><Pencil size={15} /></Act>
                          <Link to={`/painel/${r.id}`} data-testid={`open-menu-${r.id}`} className="h-8 px-2.5 rounded-lg bg-white/5 hover:bg-white/10 flex items-center text-xs font-semibold">Cardápio</Link>
                          <Act tid={`copy-${r.id}`} onClick={() => copyLink(r.slug)} title="Copiar link"><Copy size={15} /></Act>
                          <a href={`/${r.slug}`} target="_blank" rel="noreferrer" data-testid={`link-${r.id}`} className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"><ExternalLink size={15} /></a>
                          <Act tid={`reset-${r.id}`} onClick={() => resetAccess(r)} title="Redefinir acesso"><KeyRound size={15} /></Act>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3" data-testid="restaurants-cards">
          {loading && <p className="text-center text-zinc-500 py-10">Carregando...</p>}
          {!loading && filtered.length === 0 && <p className="text-center text-zinc-500 py-10">Nenhum restaurante ainda.</p>}
          {filtered.map((r) => {
            const st = STATUS[r.status] || STATUS.em_implantacao;
            return (
              <div key={r.id} className="rounded-2xl border border-white/10 bg-[#121215] p-4" data-testid={`restaurant-card-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className="hmmm-gradient rounded-xl p-[1.5px] shrink-0"><div className="h-11 w-11 rounded-[10px] overflow-hidden bg-[#141417] flex items-center justify-center">{r.logo ? <img src={mediaUrl(r.logo)} alt="" className="h-full w-full object-cover" /> : <UtensilsCrossed size={18} className="hmmm-text" />}</div></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-head font-extrabold leading-tight truncate">{r.name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">Responsável</p>
                    <p className="text-sm text-zinc-300 truncate">{r.responsible_name}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${st.cls}`}>{st.label}</span>
                </div>
                <p className="text-xs text-zinc-500 mt-2 truncate">{r.email} · vence {r.due_date || "—"}</p>
                <p className="text-[11px] text-zinc-500 mt-3 mb-1.5 uppercase tracking-wide">Ações</p>
                <div className="grid grid-cols-5 gap-2">
                  <ActCard tid={`edit-${r.id}`} onClick={() => setEditRow(r)} icon={<Pencil size={16} />} label="Editar" />
                  <Link to={`/painel/${r.id}`} data-testid={`open-menu-${r.id}`} className="flex flex-col items-center gap-1 rounded-xl bg-white/5 py-2.5 text-[11px] text-zinc-300"><UtensilsCrossed size={16} /> Cardápio</Link>
                  <ActCard tid={`copy-${r.id}`} onClick={() => copyLink(r.slug)} icon={<Copy size={16} />} label="Copiar" />
                  <a href={`/${r.slug}`} target="_blank" rel="noreferrer" data-testid={`link-${r.id}`} className="flex flex-col items-center gap-1 rounded-xl bg-white/5 py-2.5 text-[11px] text-zinc-300"><ExternalLink size={16} /> Abrir</a>
                  <ActCard tid={`reset-${r.id}`} onClick={() => resetAccess(r)} icon={<KeyRound size={16} />} label="Acesso" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Brand banner */}
        <div className="relative overflow-hidden rounded-2xl mt-5 p-6 hmmm-gradient">
          <div className="absolute -right-6 -bottom-8 opacity-30"><img src="/hmmm-icon.png" alt="" className="h-32 w-32" /></div>
          <p className="font-head text-2xl font-extrabold text-white leading-tight relative">Mais sabor para<br />grandes ideias.</p>
        </div>
          </>
        ) : (
          <LeadsView leads={leads} loading={leadsLoading} reload={loadLeads} />
        )}
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {editRow && <EditModal row={editRow} onClose={() => setEditRow(null)} onSaved={load} />}
    </div>
  );
}

function Act({ children, onClick, title, tid }) {
  return <button onClick={onClick} title={title} data-testid={tid} className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center">{children}</button>;
}
function ActCard({ icon, label, onClick, tid }) {
  return <button onClick={onClick} data-testid={tid} className="flex flex-col items-center gap-1 rounded-xl bg-white/5 hover:bg-white/10 py-2.5 text-[11px] text-zinc-300">{icon} {label}</button>;
}

const LEAD_STATUS = {
  novo: { label: "Novo", cls: "bg-sky-500/15 text-sky-300" },
  em_contato: { label: "Em contato", cls: "bg-amber-500/15 text-amber-300" },
  convertido: { label: "Convertido", cls: "bg-emerald-500/15 text-emerald-300" },
  arquivado: { label: "Arquivado", cls: "bg-zinc-600/20 text-zinc-400" },
};
function LeadsView({ leads, loading, reload }) {
  const setStatus = async (l, s) => { try { await api.put(`/admin/leads/${l.id}`, { status: s }); reload(); toast.success("Status atualizado"); } catch (e) { toast.error(apiErr(e)); } };
  const wa = (l) => { window.open(`https://wa.me/${waNumber(l.whatsapp)}`, "_blank"); };
  const [confirmDel, setConfirmDel] = useState(null);
  const del = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/leads/${confirmDel.id}`);
      toast.success("Lead excluído com sucesso.");
      setConfirmDel(null);
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (loading) return <p className="text-center text-zinc-500 py-10">Carregando...</p>;
  if (!leads.length) return <div className="rounded-2xl border border-dashed border-white/10 py-12 text-center text-sm text-zinc-500" data-testid="leads-empty">Nenhuma solicitação de orçamento ainda.</div>;
  return (
    <div className="space-y-3" data-testid="leads-list">
      {leads.map((l) => {
        const st = LEAD_STATUS[l.status] || LEAD_STATUS.novo;
        const d = new Date(l.created_at);
        return (
          <div key={l.id} className="rounded-2xl border border-white/10 bg-[#121215] p-4" data-testid={`lead-${l.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-head font-bold truncate">{l.restaurant_name}</p>
                {l.contact_name && <p className="text-xs text-zinc-400 truncate">{l.contact_name}</p>}
                <p className="text-sm text-zinc-400">{maskPhoneBR(l.whatsapp)}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {l.interest && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FF6B00]/15 text-[#FF8A3D]" data-testid={`lead-interest-${l.id}`}>{l.interest}</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-500">{l.origin || "Landing Page"}</span>
                </div>
                <p className="text-xs text-zinc-600 mt-1">{d.toLocaleDateString("pt-BR")} · {d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${st.cls}`}>{st.label}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button onClick={() => wa(l)} data-testid={`lead-wa-${l.id}`} className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 text-white" style={{ background: "#25D366" }}><MessageCircle size={14} /> Chamar no WhatsApp</button>
              <select value={l.status} onChange={(e) => setStatus(l, e.target.value)} data-testid={`lead-status-${l.id}`} className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs outline-none">
                {Object.entries(LEAD_STATUS).map(([k, v]) => <option key={k} value={k} className="bg-zinc-900">{v.label}</option>)}
              </select>
              <button onClick={() => setConfirmDel(l)} data-testid={`lead-delete-${l.id}`} title="Excluir lead permanentemente" className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-rose-500/15 text-rose-300 hover:bg-rose-500/25"><Trash2 size={14} /> Excluir</button>
            </div>
          </div>
        );
      })}
      {confirmDel && (
        <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center px-4" onClick={() => setConfirmDel(null)}>
          <div className="w-full max-w-sm bg-[#141417] border border-white/10 rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-delete-modal">
            <h3 className="font-head text-lg font-extrabold">Excluir este lead permanentemente?</h3>
            <p className="text-sm text-zinc-400 mt-2">{confirmDel.restaurant_name} · {maskPhoneBR(confirmDel.whatsapp)}</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirmDel(null)} data-testid="lead-delete-cancel" className="flex-1 rounded-full bg-white/5 hover:bg-white/10 py-3 text-sm font-bold">Cancelar</button>
              <button onClick={del} data-testid="lead-delete-confirm" className="flex-1 rounded-full bg-rose-600 hover:bg-rose-700 py-3 text-sm font-bold text-white">Excluir lead</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", responsible_name: "", email: "", phone: "", status: "em_implantacao", due_date: "", send_email: true });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/restaurants", { ...form, phone: localDigitsBR(form.phone), due_date: form.due_date || null });
      toast.success(`Restaurante criado! Senha temporária: ${data.temp_password}`, { duration: 15000 });
      onCreated();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} title="Novo restaurante">
      <form onSubmit={submit} className="space-y-3" data-testid="create-restaurant-form">
        <Field label="Nome do restaurante" value={form.name} onChange={(v) => set("name", v)} tid="cr-name" required />
        <Field label="Responsável" value={form.responsible_name} onChange={(v) => set("responsible_name", v)} tid="cr-responsible" required />
        <Field label="E-mail de acesso" type="email" value={form.email} onChange={(v) => set("email", v)} tid="cr-email" required />
        <Field label="Telefone / WhatsApp" value={form.phone} onChange={(v) => set("phone", maskPhoneBR(v))} tid="cr-phone" placeholder="(11) 99999-0000" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-zinc-400">Status</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="cr-status" className="w-full mt-1 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 outline-none">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k} className="bg-zinc-900">{v.label}</option>)}
            </select>
          </div>
          <Field label="Vencimento" type="date" value={form.due_date} onChange={(v) => set("due_date", v)} tid="cr-due" />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 pt-1">
          <input type="checkbox" checked={form.send_email} onChange={(e) => set("send_email", e.target.checked)} data-testid="cr-sendemail" /> Enviar e-mail de boas-vindas com senha temporária
        </label>
        <button disabled={loading} data-testid="cr-submit" className="w-full rounded-full hmmm-gradient text-white py-3 font-bold hmmm-glow disabled:opacity-60 mt-2">{loading ? "Criando..." : "Criar restaurante"}</button>
      </form>
    </Modal>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({ name: row.name, responsible_name: row.responsible_name, status: row.status, due_date: row.due_date || "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/admin/restaurants/${row.id}`, { ...form, due_date: form.due_date || null });
      toast.success("Conta atualizada");
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose} title={`Editar — ${row.name}`}>
      <form onSubmit={submit} className="space-y-3" data-testid="edit-restaurant-form">
        <Field label="Nome" value={form.name} onChange={(v) => set("name", v)} tid="ed-name" />
        <Field label="Responsável" value={form.responsible_name} onChange={(v) => set("responsible_name", v)} tid="ed-responsible" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-zinc-400">Status</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="ed-status" className="w-full mt-1 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5 outline-none">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k} className="bg-zinc-900">{v.label}</option>)}
            </select>
          </div>
          <Field label="Vencimento" type="date" value={form.due_date} onChange={(v) => set("due_date", v)} tid="ed-due" />
        </div>
        <button disabled={loading} data-testid="ed-submit" className="w-full rounded-full hmmm-gradient text-white py-3 font-bold hmmm-glow disabled:opacity-60 mt-2">{loading ? "Salvando..." : "Salvar alterações"}</button>
      </form>
    </Modal>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center px-4 overflow-y-auto py-8" onClick={onClose}>
      <div className="w-full max-w-md bg-[#141417] border border-white/10 rounded-3xl p-6 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head text-lg font-extrabold">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", tid, required }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-400">{label}</label>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} data-testid={tid}
        className="w-full mt-1 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-2.5 outline-none focus:border-[#FF6B00]" />
    </div>
  );
}
